/**
 * RsDataInterface Component
 *
 * Top-level interface for the national/subnational routine surveillance data
 * page. Owns the page chrome (title, description, Card layout) and the
 * graph/table visual toggle, and treats the URL PATH as the source of truth for
 * the view, selected country, and (graph-only) metric:
 *
 *   /data/rs/:level/:view/:country?/:metric?
 *
 * where :view is "plots" (default) or "table", :country is an ISO3 code or the
 * "all" token, and :metric is a metric_name (plots, all-countries mode only).
 * The selected country/metric are passed down as controlled props; the children
 * report changes back up, which this component turns into navigation so the URL
 * always reflects (and can restore) the current selection.
 */

import { useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import RsDataTable from "@/components/rs-data/RsDataTable"
import RsDataPlots from "@/components/rs-data/RsDataPlots"
import { ALL_ADMIN_UNITS_TOKEN, ALL_COUNTRIES_TOKEN } from "@/components/rs-data/rsPlotData"
import SingleSelectCombobox, { ComboboxOption } from "@/components/shared/SingleSelectCombobox"
import {
  DEFAULT_TOPIC,
  RoutineSurveillanceTopic,
} from "@/services/routineSurveillanceService"

const NATIONAL_PAGE_TITLE = "National Surveillance Data"
const SUBNATIONAL_PAGE_TITLE = "Subnational Surveillance Data"

/** The visual view modes, in display order (plots first, and the default). */
type VisualOption = "plots" | "table"

const VISUAL_OPTIONS: { value: VisualOption; label: string }[] = [
  { value: "plots", label: "Plots" },
  { value: "table", label: "Table" },
]

/** The default view when the path's :view segment is missing or unrecognised. */
const DEFAULT_VIEW: VisualOption = "plots"

/** The query-string parameter name carrying the selected topic partition. */
const TOPIC_PARAM: string = "topic"

/**
 * The topic options shown in the topic selector, in display order. The values
 * are the topic partition names the backend expects; labels are capitalised for
 * display. Both incidence and mortality are always available (they are fixed
 * partitions), so this is a static list rather than a fetched facet.
 */
const TOPIC_OPTIONS: { value: RoutineSurveillanceTopic; label: string }[] = [
  { value: "incidence", label: "Incidence" },
  { value: "mortality", label: "Mortality" },
]

/** Props for the RsDataInterface component. */
interface RsDataInterfaceProps {
  /**
   * Which surveillance level this interface shows. Controls the page title,
   * heading, and description, and is forwarded to the child views so they apply
   * the correct National/Subnational map_admin_id constraint.
   */
  level?: "national" | "subnational"
}

/**
 * Coerce the raw :view path segment into a valid VisualOption, falling back to
 * the default for anything unrecognised (or missing).
 *
 * @param raw - The raw :view path segment (may be undefined).
 * @returns The resolved view mode.
 */
function resolveView(raw: string | undefined): VisualOption {
  return raw === "plots" || raw === "table" ? raw : DEFAULT_VIEW
}

/**
 * Coerce the raw ?topic query param into a valid RoutineSurveillanceTopic,
 * falling back to the default ("incidence") for anything unrecognised or
 * missing. Keeping resolution here means a bad/absent value in the URL never
 * reaches the data layer as an invalid topic; it always resolves to a real one.
 *
 * @param raw - The raw ?topic query-string value (may be null when absent).
 * @returns The resolved topic partition.
 */
function resolveTopic(raw: string | null): RoutineSurveillanceTopic {
  return raw === "incidence" || raw === "mortality" ? raw : DEFAULT_TOPIC
}

/**
 * Routine Surveillance data interface component.
 *
 * Presents the page chrome and the graph/table toggle, resolves the view /
 * country / metric from the URL path, and renders the matching child view with
 * those values controlled from the URL.
 *
 * @param props - Component props.
 * @param props.level - Whether to present the National or Subnational view.
 *        Drives the titles/labels and the constraint applied inside the child
 *        views. Defaults to "national".
 */
export default function RsDataInterface({
  level = "national",
}: RsDataInterfaceProps): React.JSX.Element {
  const navigate = useNavigate()
  const params = useParams<{
    view?: string
    country?: string
    // Subnational-only path segments. Absent (undefined) on the national view.
    adminLevel?: string
    adminUnit?: string
    metric?: string
  }>()
  const [searchParams] = useSearchParams()

  const view: VisualOption = resolveView(params.view)

  // Whether this is the subnational view. Subnational carries two extra path
  // dimensions (admin level + admin unit) and forbids the "All Countries"
  // selection, so several derivations below branch on it.
  const isSubnational: boolean = level === "subnational"

  // The selected topic partition, read from the ?topic query param (defaults to
  // "incidence"). Kept in the URL — rather than component state — so a topic
  // view is linkable and survives a refresh, matching how the view/country/
  // metric selections round-trip through the path.
  const topic: RoutineSurveillanceTopic = resolveTopic(searchParams.get(TOPIC_PARAM))

  // The base path for this level, onto which the segments are appended.
  const basePath: string = `/data/rs/${level}`

  // Selected country from the path. On the national view it defaults to the
  // "all" token (All Countries); on the subnational view "All Countries" is not
  // allowed, so it defaults to empty (no country chosen yet) instead.
  const selectedCountry: string =
    params.country ?? (isSubnational ? "" : ALL_COUNTRIES_TOKEN)

  // Subnational-only selections, read from the path. Empty admin level means
  // none chosen yet; admin unit defaults to the "All Admin Units" token. Both
  // are unused on the national view.
  const selectedAdminLevel: string = isSubnational ? (params.adminLevel ?? "") : ""
  const selectedAdminUnit: string = isSubnational
    ? (params.adminUnit ?? ALL_ADMIN_UNITS_TOKEN)
    : ALL_ADMIN_UNITS_TOKEN

  // Selected metric from the path (plots view). Empty when absent. On the
  // national view metric applies in All-Countries mode; on subnational it
  // applies in All-Admin-Units mode.
  const selectedMetric: string = params.metric ?? ""

  // Derive the active title/description from the level. Both views currently
  // share the same data source; only the labelling differs.
  const pageTitle: string =
    level === "subnational" ? SUBNATIONAL_PAGE_TITLE : NATIONAL_PAGE_TITLE
  const pageDescription: string =
    level === "subnational"
      ? "Subnational routine surveillance records for the latest data snapshot."
      : "National routine surveillance records for the latest data snapshot."

  /**
   * Build the URL for a given view/country/metric combination. Segments are
   * only included up to the deepest meaningful one (metric implies country),
   * and each is URI-encoded so values with spaces (e.g. metric names) are safe.
   *
   * @param next - The view/country/metric to encode (each optional).
   * @returns The path string for that combination.
   */
  const buildPath = useCallback((next: {
    view?: VisualOption
    country?: string
    adminLevel?: string
    adminUnit?: string
    metric?: string
    topic?: RoutineSurveillanceTopic
  }): string => {
    const v: VisualOption = next.view ?? view
    const country: string | undefined = next.country
    const metric: string | undefined = next.metric
    // Topic falls back to the current topic so any navigation preserves it
    // unless the caller is explicitly changing it.
    const t: RoutineSurveillanceTopic = next.topic ?? topic

    let path = `${basePath}/${v}`

    if (isSubnational) {
      // Subnational segment order: country / adminLevel / adminUnit / metric.
      // Each segment is only appended once the ones before it are present, so a
      // partial selection produces a valid shorter path (deeper segments imply
      // the shallower ones).
      const adminLevel: string | undefined = next.adminLevel
      const adminUnit: string | undefined = next.adminUnit
      if (country !== undefined && country !== "") {
        path += `/${encodeURIComponent(country)}`
        if (adminLevel !== undefined && adminLevel !== "") {
          path += `/${encodeURIComponent(adminLevel)}`
          // Admin unit only belongs alongside an admin level. Defaults to the
          // "All Admin Units" token so the segment is always present once a
          // level is chosen (keeping the metric segment position stable).
          const unit: string = adminUnit ?? ALL_ADMIN_UNITS_TOKEN
          path += `/${encodeURIComponent(unit)}`
          // Metric only belongs in All-Admin-Units mode (unit === "all").
          if (
            unit === ALL_ADMIN_UNITS_TOKEN &&
            metric !== undefined &&
            metric !== ""
          ) {
            path += `/${encodeURIComponent(metric)}`
          }
        }
      }
    } else {
      // National segment order: country / metric.
      if (country !== undefined && country !== "") {
        path += `/${encodeURIComponent(country)}`
        // Metric only belongs in the URL alongside a country segment.
        if (metric !== undefined && metric !== "") {
          path += `/${encodeURIComponent(metric)}`
        }
      }
    }

    // Carry the topic as a query param on every path. It is only omitted for the
    // default ("incidence") so the URL stays clean when nothing is customised,
    // mirroring the default the backend applies when topic is absent.
    if (t !== DEFAULT_TOPIC) {
      path += `?${TOPIC_PARAM}=${encodeURIComponent(t)}`
    }
    return path
  }, [basePath, view, topic, isSubnational])

  /**
   * Switch the view (graph/table), preserving the current country. The metric
   * is intentionally dropped when switching to the table (the table has no
   * metric selection); it is preserved when staying within/So switching to
   * graph keeps it.
   *
   * @param nextView - The view to switch to.
   */
  const handleViewChange = useCallback((nextView: VisualOption): void => {
    navigate(
      buildPath({
        view: nextView,
        country: selectedCountry,
        // Subnational carries admin level + unit across a view switch so the
        // table and plots stay on the same selection. (buildPath ignores these
        // on the national view.)
        adminLevel: selectedAdminLevel,
        adminUnit: selectedAdminUnit,
        metric: nextView === "plots" ? selectedMetric : undefined,
      })
    )
  }, [
    navigate,
    buildPath,
    selectedCountry,
    selectedAdminLevel,
    selectedAdminUnit,
    selectedMetric,
  ])

  /**
   * Update the selected country from a child view, navigating to the new path.
   * Changing country clears the metric (a metric may not exist for the new
   * country/selection), letting the child re-establish it if appropriate.
   *
   * @param nextCountry - The new country token (ISO3 or the "all" token).
   */
  const handleCountryChange = useCallback((nextCountry: string): void => {
    // Changing country clears every downstream selection: on subnational the
    // admin level, admin unit, and metric all belong to the previous country
    // and may not exist for the new one, so buildPath drops them (only the
    // country segment is emitted). On national this simply clears the metric.
    navigate(buildPath({ country: nextCountry }))
  }, [navigate, buildPath])

  /**
   * Update the selected admin level from a child view (subnational only),
   * navigating to the new path. Changing the admin level clears the admin unit
   * and metric — the units available differ per level, so a unit selected under
   * the old level may not exist under the new one. buildPath re-defaults the
   * admin unit to "All Admin Units" and drops the metric.
   *
   * @param nextAdminLevel - The chosen non-zero admin level (numeric string).
   */
  const handleAdminLevelChange = useCallback((nextAdminLevel: string): void => {
    navigate(
      buildPath({
        country: selectedCountry,
        adminLevel: nextAdminLevel,
        // adminUnit/metric intentionally omitted so they reset.
      })
    )
  }, [navigate, buildPath, selectedCountry])

  /**
   * Update the selected admin unit from a child view (subnational only),
   * navigating to the new path. Selecting a specific unit drops the metric
   * (specific-unit mode plots every metric and has no metric selection);
   * selecting "All Admin Units" also starts with no metric until the user picks
   * one.
   *
   * @param nextAdminUnit - The chosen unit's map_admin_id, or the "all" token.
   */
  const handleAdminUnitChange = useCallback((nextAdminUnit: string): void => {
    navigate(
      buildPath({
        country: selectedCountry,
        adminLevel: selectedAdminLevel,
        adminUnit: nextAdminUnit,
        // metric intentionally omitted so it resets on a unit change.
      })
    )
  }, [navigate, buildPath, selectedCountry, selectedAdminLevel])

  /**
   * Update the selected metric from the graph view, navigating to the new path.
   *
   * @param nextMetric - The new metric_name ("" to clear).
   */
  const handleMetricChange = useCallback((nextMetric: string): void => {
    navigate(
      buildPath({
        country: selectedCountry,
        // Carry the subnational selections so the metric change stays within the
        // current country/level/unit. (Ignored on the national view.)
        adminLevel: selectedAdminLevel,
        adminUnit: selectedAdminUnit,
        metric: nextMetric,
      })
    )
  }, [navigate, buildPath, selectedCountry, selectedAdminLevel, selectedAdminUnit])

  /**
   * Update the selected topic partition, navigating to the new URL (?topic=...).
   *
   * Changing topic clears the metric: the set of metrics differs between
   * incidence and mortality, so a metric selected under one topic may not exist
   * under the other. Dropping it lets the child view re-establish a valid metric
   * for the new topic rather than carrying over a now-invalid one. The country
   * is preserved (countries exist across both topics).
   *
   * @param nextTopic - The chosen topic partition ("incidence" or "mortality").
   */
  const handleTopicChange = useCallback((nextTopic: string): void => {
    // Ignore anything outside the known topics (the combobox should never emit
    // one, but validate rather than trust) and no-op re-selection of the current.
    if (
      (nextTopic !== "incidence" && nextTopic !== "mortality") ||
      nextTopic === topic
    ) {
      return
    }
    navigate(
      buildPath({
        country: selectedCountry,
        // Preserve the subnational admin level + unit across a topic change
        // (they exist across both topics); only the metric is topic-specific.
        adminLevel: selectedAdminLevel,
        adminUnit: selectedAdminUnit,
        // Drop the metric on topic change — see docstring.
        metric: undefined,
        topic: nextTopic,
      })
    )
  }, [
    navigate,
    buildPath,
    selectedCountry,
    selectedAdminLevel,
    selectedAdminUnit,
    topic,
  ])

  // The topic options mapped into the {value,label} shape SingleSelectCombobox
  // expects. Static (both partitions always exist), so computed inline.
  const topicComboboxOptions: ComboboxOption[] = TOPIC_OPTIONS.map(
    ({ value, label }) => ({ value, label })
  )

  return (
    <>
      <title>{pageTitle} - MAP Apps Platform</title>
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardHeader className="pb-2">
          {/* Title/description on the left, graph/table toggle pinned to the
              top right and vertically centred against the heading block. */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>
                <h1>{pageTitle}</h1>
              </CardTitle>
              <CardDescription>
                {pageDescription}
              </CardDescription>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              {/* Topic selector: chooses incidence vs mortality. Sits left of
                  the plots/table toggle (and thus left of the metric selector
                  inside the plots view). Both the table and plots views below
                  are scoped to this topic, and the metric options within the
                  plots view are refetched per-topic. */}
              <div className="flex items-center gap-2">
                <label
                  htmlFor="rs-topic-select"
                  className="text-sm text-muted-foreground whitespace-nowrap"
                >
                  Topic
                </label>
                <div className="w-[160px]">
                  <SingleSelectCombobox
                    id="rs-topic-select"
                    options={topicComboboxOptions}
                    value={topic}
                    onChange={handleTopicChange}
                    placeholder="Select a topic"
                    emptyMessage="No topics found."
                    showClear={false}
                  />
                </div>
              </div>
              {VISUAL_OPTIONS.map(({ value, label }) => (
                <Link
                  key={value}
                  to={buildPath({
                    view: value,
                    country: selectedCountry,
                    adminLevel: selectedAdminLevel,
                    adminUnit: selectedAdminUnit,
                    metric: value === "plots" ? selectedMetric : undefined,
                  })}
                  onClick={(e) => {
                    // Let Link build/handle the href for accessibility, but keep
                    // navigation going through the shared handler for clarity.
                    e.preventDefault()
                    handleViewChange(value)
                  }}
                  className={`no-underline text-sm/3.5 h-8 inline-flex items-center gap-1.5 px-2.5 py-1.5 font-semibold border transition-all ${
                    view === value
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-border hover:bg-accent/40"
                  }`}
                >
                  <span className="text-muted-foreground">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col min-h-0">
          {view === "table" ? (
            <RsDataTable
              level={level}
              topic={topic}
              selectedCountry={selectedCountry}
              selectedAdminLevel={selectedAdminLevel}
              selectedAdminUnit={selectedAdminUnit}
              onCountryChange={handleCountryChange}
              onAdminLevelChange={handleAdminLevelChange}
              onAdminUnitChange={handleAdminUnitChange}
            />
          ) : (
            <RsDataPlots
              level={level}
              topic={topic}
              selectedCountry={selectedCountry}
              selectedAdminLevel={selectedAdminLevel}
              selectedAdminUnit={selectedAdminUnit}
              selectedMetric={selectedMetric}
              onCountryChange={handleCountryChange}
              onAdminLevelChange={handleAdminLevelChange}
              onAdminUnitChange={handleAdminUnitChange}
              onMetricChange={handleMetricChange}
            />
          )}
        </CardContent>
      </Card>
    </>
  )
}
